/**
 * FanPollsTab - Admin tab for managing fan polls on game pages
 * Allows creating polls with questions and options, associating them with games
 */
import { useState, useEffect } from 'react';
import { 
  Plus, Trash2, Edit2, Save, X, Loader2, BarChart2, 
  Copy, RefreshCw, Eye, EyeOff, Vote, Calendar, Trophy
} from 'lucide-react';
import { Button } from '../ui/button';
import { API, authHeaders } from './utils';

export const FanPollsTab = () => {
  const [polls, setPolls] = useState([]);
  const [broadcasts, setBroadcasts] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  
  // Form state
  const [showForm, setShowForm] = useState(false);
  const [editingPoll, setEditingPoll] = useState(null);
  const [formData, setFormData] = useState({
    question: '',
    options: ['', ''],
    broadcast_id: '',
    event_id: '',
    expires_at: '',
    is_active: true
  });

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [pollsRes, broadcastsRes, eventsRes] = await Promise.all([
        fetch(`${API}/fan-polls/admin/list`, { headers: authHeaders() }),
        fetch(`${API}/broadcasts/schedule`, { headers: authHeaders() }),
        fetch(`${API}/events?include_unpublished=true`, { headers: authHeaders() })
      ]);
      
      if (pollsRes.ok) setPolls(await pollsRes.json());
      if (broadcastsRes.ok) setBroadcasts(await broadcastsRes.json());
      if (eventsRes.ok) setEvents(await eventsRes.json());
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate
    if (!formData.question.trim()) {
      setError('Question is required');
      return;
    }
    
    const validOptions = formData.options.filter(o => o.trim());
    if (validOptions.length < 2) {
      setError('At least 2 options are required');
      return;
    }

    setSaving(true);
    setError('');

    try {
      const payload = {
        question: formData.question.trim(),
        options: validOptions,
        broadcast_id: formData.broadcast_id || null,
        event_id: formData.event_id || null,
        expires_at: formData.expires_at || null,
        is_active: formData.is_active
      };

      const url = editingPoll 
        ? `${API}/fan-polls/admin/${editingPoll.id}`
        : `${API}/fan-polls/admin`;
      
      const res = await fetch(url, {
        method: editingPoll ? 'PUT' : 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        await fetchAll();
        resetForm();
      } else {
        const data = await res.json();
        setError(data.detail || 'Failed to save poll');
      }
    } catch (err) {
      setError('Failed to save poll');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (poll) => {
    setEditingPoll(poll);
    setFormData({
      question: poll.question || '',
      options: poll.options?.map(o => o.text) || ['', ''],
      broadcast_id: poll.broadcast_id || '',
      event_id: poll.event_id || '',
      expires_at: poll.expires_at ? poll.expires_at.split('T')[0] : '',
      is_active: poll.is_active !== false
    });
    setShowForm(true);
  };

  const handleDelete = async (pollId) => {
    if (!window.confirm('Delete this poll?')) return;
    
    try {
      const res = await fetch(`${API}/fan-polls/admin/${pollId}`, {
        method: 'DELETE',
        headers: authHeaders()
      });
      if (res.ok) {
        await fetchAll();
      }
    } catch (err) {
      console.error('Error deleting poll:', err);
    }
  };

  const handleToggleActive = async (poll) => {
    try {
      await fetch(`${API}/fan-polls/admin/${poll.id}`, {
        method: 'PUT',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_active: !poll.is_active })
      });
      await fetchAll();
    } catch (err) {
      console.error('Error toggling poll:', err);
    }
  };

  const handleDuplicate = async (pollId) => {
    try {
      const res = await fetch(`${API}/fan-polls/admin/${pollId}/duplicate`, {
        method: 'POST',
        headers: authHeaders()
      });
      if (res.ok) {
        await fetchAll();
      }
    } catch (err) {
      console.error('Error duplicating poll:', err);
    }
  };

  const handleResetVotes = async (pollId) => {
    if (!window.confirm('Reset all votes on this poll? This cannot be undone.')) return;
    
    try {
      const res = await fetch(`${API}/fan-polls/admin/${pollId}/reset`, {
        method: 'POST',
        headers: authHeaders()
      });
      if (res.ok) {
        await fetchAll();
      }
    } catch (err) {
      console.error('Error resetting votes:', err);
    }
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingPoll(null);
    setFormData({
      question: '',
      options: ['', ''],
      broadcast_id: '',
      event_id: '',
      expires_at: '',
      is_active: true
    });
    setError('');
  };

  const addOption = () => {
    if (formData.options.length < 6) {
      setFormData({ ...formData, options: [...formData.options, ''] });
    }
  };

  const removeOption = (index) => {
    if (formData.options.length > 2) {
      const newOptions = formData.options.filter((_, i) => i !== index);
      setFormData({ ...formData, options: newOptions });
    }
  };

  const updateOption = (index, value) => {
    const newOptions = [...formData.options];
    newOptions[index] = value;
    setFormData({ ...formData, options: newOptions });
  };

  const getBroadcastTitle = (id) => {
    const broadcast = broadcasts.find(b => b.id === id);
    return broadcast ? broadcast.title : 'Unknown Game';
  };

  const getEventName = (id) => {
    const event = events.find(e => e.id === id);
    return event ? event.name : 'Unknown Event';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="fan-polls-admin-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <Vote className="w-6 h-6 text-orange-500" />
            Fan Polls
          </h2>
          <p className="text-zinc-500 text-sm">Create polls for game pages to engage fans</p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={fetchAll} variant="outline" size="sm">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          {!showForm && (
            <Button onClick={() => setShowForm(true)} className="bg-orange-500 hover:bg-orange-600">
              <Plus className="w-4 h-4 mr-2" />
              Create Poll
            </Button>
          )}
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/50 text-red-500 px-4 py-3 rounded-lg flex items-center justify-between">
          <span>{error}</span>
          <button onClick={() => setError('')}><X className="w-4 h-4" /></button>
        </div>
      )}

      {/* Create/Edit Form */}
      {showForm && (
        <form onSubmit={handleSubmit} className="bg-zinc-900 rounded-xl p-6 space-y-4 border border-white/10">
          <h3 className="text-white font-bold flex items-center gap-2">
            <Vote className="w-5 h-5 text-orange-500" />
            {editingPoll ? 'Edit Poll' : 'Create New Poll'}
          </h3>

          {/* Question */}
          <div>
            <label className="block text-zinc-400 text-sm mb-1">Poll Question *</label>
            <input
              type="text"
              value={formData.question}
              onChange={(e) => setFormData({ ...formData, question: e.target.value })}
              placeholder="e.g., Who will win today's game?"
              className="w-full bg-zinc-800 border border-white/10 text-white px-4 py-2 rounded"
              required
            />
          </div>

          {/* Options */}
          <div>
            <label className="block text-zinc-400 text-sm mb-2">Answer Options * (2-6 options)</label>
            <div className="space-y-2">
              {formData.options.map((option, index) => (
                <div key={index} className="flex gap-2">
                  <input
                    type="text"
                    value={option}
                    onChange={(e) => updateOption(index, e.target.value)}
                    placeholder={`Option ${index + 1}`}
                    className="flex-1 bg-zinc-800 border border-white/10 text-white px-4 py-2 rounded"
                  />
                  {formData.options.length > 2 && (
                    <button
                      type="button"
                      onClick={() => removeOption(index)}
                      className="p-2 hover:bg-red-500/20 rounded text-red-500"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
            {formData.options.length < 6 && (
              <button
                type="button"
                onClick={addOption}
                className="mt-2 text-sm text-orange-500 hover:text-orange-400 flex items-center gap-1"
              >
                <Plus className="w-4 h-4" /> Add Option
              </button>
            )}
          </div>

          {/* Association */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-zinc-400 text-sm mb-1">
                <Calendar className="w-4 h-4 inline mr-1" />
                Associate with Game
              </label>
              <select
                value={formData.broadcast_id}
                onChange={(e) => setFormData({ ...formData, broadcast_id: e.target.value })}
                className="w-full bg-zinc-800 border border-white/10 text-white px-4 py-2 rounded"
              >
                <option value="">-- No Game Selected --</option>
                {broadcasts.map(b => (
                  <option key={b.id} value={b.id}>{b.title} ({b.scheduled_date})</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-zinc-400 text-sm mb-1">
                <Trophy className="w-4 h-4 inline mr-1" />
                Associate with Event
              </label>
              <select
                value={formData.event_id}
                onChange={(e) => setFormData({ ...formData, event_id: e.target.value })}
                className="w-full bg-zinc-800 border border-white/10 text-white px-4 py-2 rounded"
              >
                <option value="">-- No Event Selected --</option>
                {events.map(e => (
                  <option key={e.id} value={e.id}>{e.name}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Expiration & Active */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-zinc-400 text-sm mb-1">Expiration Date (optional)</label>
              <input
                type="date"
                value={formData.expires_at}
                onChange={(e) => setFormData({ ...formData, expires_at: e.target.value })}
                className="w-full bg-zinc-800 border border-white/10 text-white px-4 py-2 rounded"
              />
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="rounded bg-zinc-800 border-white/20"
                />
                <span className="text-zinc-400 text-sm">Active (visible to users)</span>
              </label>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-2 pt-2">
            <Button type="submit" disabled={saving} className="bg-orange-500 hover:bg-orange-600">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              {editingPoll ? 'Update Poll' : 'Create Poll'}
            </Button>
            <Button type="button" variant="outline" onClick={resetForm}>
              Cancel
            </Button>
          </div>
        </form>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-zinc-900 rounded-lg p-4 border border-white/5">
          <p className="text-zinc-500 text-xs uppercase">Total Polls</p>
          <p className="text-2xl font-bold text-white">{polls.length}</p>
        </div>
        <div className="bg-zinc-900 rounded-lg p-4 border border-white/5">
          <p className="text-zinc-500 text-xs uppercase">Active</p>
          <p className="text-2xl font-bold text-green-500">{polls.filter(p => p.is_active).length}</p>
        </div>
        <div className="bg-zinc-900 rounded-lg p-4 border border-white/5">
          <p className="text-zinc-500 text-xs uppercase">Total Votes</p>
          <p className="text-2xl font-bold text-orange-500">{polls.reduce((sum, p) => sum + (p.total_votes || 0), 0)}</p>
        </div>
        <div className="bg-zinc-900 rounded-lg p-4 border border-white/5">
          <p className="text-zinc-500 text-xs uppercase">Expired</p>
          <p className="text-2xl font-bold text-zinc-400">{polls.filter(p => p.is_expired).length}</p>
        </div>
      </div>

      {/* Polls List */}
      {polls.length === 0 ? (
        <div className="text-center py-12 bg-zinc-900/50 rounded-xl border border-white/5">
          <Vote className="w-12 h-12 mx-auto mb-3 text-zinc-600" />
          <p className="text-zinc-500">No fan polls yet</p>
          <p className="text-zinc-600 text-sm">Create polls to engage fans on game pages</p>
        </div>
      ) : (
        <div className="space-y-4">
          {polls.map(poll => (
            <div 
              key={poll.id} 
              className={`bg-zinc-900 rounded-xl border overflow-hidden ${
                poll.is_active ? 'border-white/10' : 'border-red-500/30 opacity-70'
              }`}
            >
              {/* Poll Header */}
              <div className="p-4 flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    {poll.is_active ? (
                      <span className="px-2 py-0.5 bg-green-500/20 text-green-500 text-xs font-bold rounded">ACTIVE</span>
                    ) : (
                      <span className="px-2 py-0.5 bg-zinc-700 text-zinc-400 text-xs font-bold rounded">INACTIVE</span>
                    )}
                    {poll.is_expired && (
                      <span className="px-2 py-0.5 bg-red-500/20 text-red-500 text-xs font-bold rounded">EXPIRED</span>
                    )}
                    {poll.broadcast_id && (
                      <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 text-xs rounded flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {getBroadcastTitle(poll.broadcast_id)}
                      </span>
                    )}
                    {poll.event_id && (
                      <span className="px-2 py-0.5 bg-yellow-500/20 text-yellow-400 text-xs rounded flex items-center gap-1">
                        <Trophy className="w-3 h-3" />
                        {getEventName(poll.event_id)}
                      </span>
                    )}
                  </div>
                  <h4 className="text-white font-bold text-lg">{poll.question}</h4>
                  <p className="text-zinc-500 text-sm mt-1">
                    {poll.total_votes} votes • Created {new Date(poll.created_at).toLocaleDateString()}
                    {poll.expires_at && ` • Expires ${new Date(poll.expires_at).toLocaleDateString()}`}
                  </p>
                </div>
                
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => handleToggleActive(poll)}
                    className={`p-2 rounded-lg transition-colors ${poll.is_active ? 'hover:bg-red-500/20' : 'hover:bg-green-500/20'}`}
                    title={poll.is_active ? 'Deactivate' : 'Activate'}
                  >
                    {poll.is_active ? <EyeOff className="w-4 h-4 text-zinc-400" /> : <Eye className="w-4 h-4 text-green-500" />}
                  </button>
                  <button
                    onClick={() => handleDuplicate(poll.id)}
                    className="p-2 hover:bg-zinc-800 rounded-lg transition-colors"
                    title="Duplicate"
                  >
                    <Copy className="w-4 h-4 text-zinc-400" />
                  </button>
                  <button
                    onClick={() => handleResetVotes(poll.id)}
                    className="p-2 hover:bg-zinc-800 rounded-lg transition-colors"
                    title="Reset Votes"
                  >
                    <RefreshCw className="w-4 h-4 text-zinc-400" />
                  </button>
                  <button
                    onClick={() => handleEdit(poll)}
                    className="p-2 hover:bg-zinc-800 rounded-lg transition-colors"
                    title="Edit"
                  >
                    <Edit2 className="w-4 h-4 text-zinc-400" />
                  </button>
                  <button
                    onClick={() => handleDelete(poll.id)}
                    className="p-2 hover:bg-red-500/20 rounded-lg transition-colors"
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </button>
                </div>
              </div>
              
              {/* Poll Results */}
              <div className="px-4 pb-4">
                <div className="space-y-2">
                  {poll.options?.map(option => (
                    <div key={option.id} className="flex items-center gap-3">
                      <div className="flex-1">
                        <div className="flex items-center justify-between text-sm mb-1">
                          <span className="text-zinc-300">{option.text}</span>
                          <span className="text-zinc-500">{option.votes} votes ({option.percentage || 0}%)</span>
                        </div>
                        <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-orange-500 rounded-full transition-all duration-300"
                            style={{ width: `${option.percentage || 0}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default FanPollsTab;
